import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { LocalStorageKeys } from '../enums/LocalStorageKeys';
import { TranslateService } from '@ngx-translate/core';

export interface AlertData {
  text: string;
  type: AlertType;
  duration?: number;
}

export enum AlertType {
  SUCCESS = 1,
  ERROR = 2
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {

  /*
  
  EXAMPLE OF CALLING

  const AlertData: AlertData = {
        text: this.translateService.instant('ERROR_API.ORDER_SERVICE.' + error?.error.ErrorCode),
        type: AlertType.ERROR,
        duration: 7000
      }
      this.alertService.showAlert(AlertData);
  
  */
  private alertSubject = new BehaviorSubject<AlertData | null>(null);
  alert$ = this.alertSubject.asObservable();

  private queue: AlertData[] = [];
  private processing = false;

  // Gap entre alertas en ms
  private readonly gapMs = 300;

  constructor(
    private localStorageService: LocalStorageService,
    private translateService: TranslateService
  ) {
    // Comentado según tu último snippet
  }

  showAlert(obj: AlertData): void {
    // Clonamos para evitar referencias compartidas/modificaciones externas
    const alert: AlertData = {
      text: obj.text,
      type: obj.type,
      duration: obj.duration ?? 5000
    };

    this.queue.push(alert);
    this.startProcessing();
  }

  private async startProcessing(): Promise<void> {
    if (this.processing) return;
    this.processing = true;

    while (this.queue.length > 0) {
      const nextAlert = this.queue.shift()!;
      this.displayAlert(nextAlert);

      // Pequeña pausa para dejar que Angular renderice/ejecute animaciones
      // (en algunos casos necesario para que la UI muestre la alerta antes de contar tiempo)
      await new Promise<void>(res => setTimeout(res, 0));

      // Espera la duración solicitada
      const duration = nextAlert.duration ?? 5000;
      await new Promise<void>(res => setTimeout(res, duration));

      // Limpia la alerta
      this.clearAlert();

      // Espera el gap entre alertas
      await new Promise<void>(res => setTimeout(res, this.gapMs));
    }

    this.processing = false;
  }

  private displayAlert(alert: AlertData): void {
    this.alertSubject.next(alert);
    // si quieres localStorage, lo recuperas aquí
    // this.localStorageService.set(LocalStorageKeys.Alert, alert);
  }

  clearAlert(): void {
    this.alertSubject.next(null);
    // this.localStorageService.remove(LocalStorageKeys.Alert);
  }

  successMessage(msg: string, duration: number = 5000): void {
    const translateMessage = msg;
    const alert: AlertData = {
      text: translateMessage,
      type: AlertType.SUCCESS,
      duration
    };
    this.showAlert(alert);
  }

  throwErrorMessage(msg: string, duration: number = 5000): void {
    const translateMessage = msg;
    const alert: AlertData = {
      text: translateMessage,
      type: AlertType.ERROR,
      duration
    };
    this.showAlert(alert);
  }



  // TO REFACTOR | OLD CODE WORKING

  private showAlertSubject = new BehaviorSubject<boolean>(false);
  showAlert$ = this.showAlertSubject.asObservable();

  setShowAlert(value: boolean) {
    this.showAlertSubject.next(value);
    this.localStorageService.set(LocalStorageKeys.ShowUpsertVehicleAlert, value)
  }

  shouldShowAlert(): boolean {
    const storedValue = this.localStorageService.get<boolean>(LocalStorageKeys.ShowUpsertVehicleAlert)
    return !!storedValue;
  }

  resetAlert() {
    this.showAlertSubject.next(false);
    this.localStorageService.remove(LocalStorageKeys.ShowUpsertVehicleAlert)
  }
}













